import type { AppConfig } from "@studystraight/shared-config";
import type { DetectionQuality } from "@studystraight/vision-core";

import type { PerfSnapshot } from "../../platform/vision/InferenceLoop.js";
import { hipVisibilityRatio, spread, type DebugSample } from "./debugMetrics.js";

function num(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  return value.toFixed(digits);
}

export interface MetricsHudProps {
  sample: DebugSample | null;
  samples: readonly DebugSample[];
  perf: PerfSnapshot | null;
  quality: DetectionQuality | null;
  config: AppConfig;
}

/**
 * HUD số liệu — Prompt.md §25, kèm ba nhóm phục vụ verification của DU #1–#4.
 * Hàng nào có ghi chú `DU #n` là hàng phải đọc khi chạy kịch bản verify trong plan.
 */
export function MetricsHud({
  sample,
  samples,
  perf,
  quality,
  config,
}: MetricsHudProps): React.JSX.Element {
  const minVis = config.posture.minLandmarkVisibility;
  const rows: [string, string][] = [
    ["placement", config.placement],
    ["confidence", num(sample?.confidence, 2)],
    ["missing", quality?.missing.length ? quality.missing.join(", ") : "—"],
    ["headAngle 2D", `${num(sample?.headAngle2D)}°`],
    // DU #4 — hai cột này phải cùng dấu và cùng xu hướng, nếu lệch thì công thức 2D sai.
    ["headAngle world (DU #4)", `${num(sample?.headAnglePitchWorld)}°`],
    ["torsoAngle 2D", `${num(sample?.torsoAngle2D)}°`],
    ["shoulderTilt", `${num(sample?.shoulderTilt)}°`],
    // DU #2 — chạy 3 kịch bản trong plan và đọc `spread` của earDistance/shoulderWidth.
    ["earDistance (DU #2)", num(sample?.earDistance, 3)],
    ["shoulderWidth (DU #2)", num(sample?.shoulderWidth, 3)],
    ["spread ear / shoulder 60s", `${num(spread(samples, "earDistance"), 3)} / ${num(spread(samples, "shoulderWidth"), 3)}`],
    // Quyết định #21 — ngưỡng nghi ngờ sai placement được chốt bằng chính con số này.
    ["shoulderWidth/earDistance", num(sample?.shoulderEarRatio, 2)],
    ["placement mismatch", quality?.placementMismatch?.suspected ?? "—"],
    // DU #3 — dưới ~0.6 thì `torsoAngle` không dùng được ở placement này.
    ["hipVisibility 60s (DU #3)", num(hipVisibilityRatio(samples, minVis), 2)],
    // DU #1 — p95 > 60ms là mốc quyết định có cần chuyển sang Web Worker.
    ["fps target / actual (DU #1)", `${num(perf?.targetFps, 0)} / ${num(perf?.actualFps)}`],
    ["inference p50 / p95 (DU #1)", `${num(perf?.inferenceP50Ms)} / ${num(perf?.inferenceP95Ms)} ms`],
    ["dropped frames", num(perf?.droppedFrames, 0)],
    ["samples in buffer", String(samples.length)],
  ];

  return (
    <dl className="hud">
      {rows.map(([label, value]) => (
        <div className="hud__row" key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}
